import { Component } from "./component";
import aiLogger from './ai-logger';
import { readSSEStream } from '../services/ai-stream-reader';
import { getCsrfToken, getPageId, findEditorIframe, fixCodeBlockEscapes, denormalizeHtml, stripChatCodeWrappers } from '../services/ai-editor-utils';

/**
 * Interface para componente disponível na API
 */
interface SimpleComponent {
    hash: string;
    tag: string;
    label: string;
}

/**
 * AiSelectionImprove — Componente para melhoria de seleção de texto com IA.
 *
 * Arquitetura:
 * - Escuta mouseup no iframe do editor TinyMCE para detectar seleção
 * - Exibe dropdown flutuante "Melhorar com IA" ao selecionar texto
 * - Abre modal com instrução, selector de componente de referência
 * - Streaming da melhoria via POST /ai/improve-selection
 * - Aplica resultado substituindo a seleção no editor
 */
export class AiSelectionImprove extends Component {
    protected modal: any = null;
    protected modalEl: HTMLElement | null = null;
    protected initialized: boolean = false;
    protected dropdown: HTMLElement | null = null;
    protected selectedHtml: string = '';
    protected selectedRange: Range | null = null;
    protected abortController: AbortController | null = null;
    protected componentsList: SimpleComponent[] = [];
    protected isGenerating: boolean = false;
    protected resultHtml: string = '';
    protected editorDocument: Document | null = null;

    // DOM refs
    protected selectedTextPreview: HTMLElement | null = null;
    protected instructionInput: HTMLTextAreaElement | null = null;
    protected componentSelect: HTMLSelectElement | null = null;
    protected thinkingEl: HTMLElement | null = null;
    protected thinkingText: HTMLElement | null = null;
    protected resultSection: HTMLElement | null = null;
    protected resultPreview: HTMLElement | null = null;
    protected generateBtn: HTMLElement | null = null;
    protected applyBtn: HTMLElement | null = null;
    protected cancelBtn: HTMLElement | null = null;

    setup() {
        // Espera o editor ser inicializado, depois instala o listener
        this.waitForEditor();
    }

    /**
     * Aguarda o editor estar pronto e instala o listener de seleção.
     * Verifica a cada 500ms se o iframe do editor está disponível.
     */
    protected waitForEditor(): void {
        let attempts = 0;
        const maxAttempts = 60; // 30 segundos

        const interval = setInterval(() => {
            attempts++;
            if (attempts > maxAttempts) {
                clearInterval(interval);
                return;
            }

            const editorFrame = this.getEditorIframe();
            if (editorFrame) {
                clearInterval(interval);
                this.installSelectionListener(editorFrame);
            }
        }, 500);
    }

    /**
     * Busca o iframe do editor TinyMCE.
     */
    protected getEditorIframe(): HTMLIFrameElement | null {
        return findEditorIframe();
    }

    /**
     * Instala o listener de mouseup no documento do iframe (editor).
     */
    protected installSelectionListener(iframe: HTMLIFrameElement): void {
        const doc = iframe.contentDocument;
        if (!doc) return;

        this.editorDocument = doc;

        // Criar dropdown flutuante
        this.createDropdown();

        // Escutar mouseup no documento do editor
        doc.addEventListener('mouseup', (e: MouseEvent) => {
            // Delay para a seleção ser finalizada
            setTimeout(() => this.handleSelectionChange(e), 100);
        });

        // Escutar keyup para seleção via teclado (Shift+Arrows)
        doc.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.shiftKey) {
                setTimeout(() => this.handleSelectionChange(e as any), 100);
            }
        });

        // Esconder dropdown quando clicar fora
        doc.addEventListener('mousedown', () => {
            this.hideDropdown();
        });
    }

    /**
     * Cria o dropdown flutuante que aparece sobre a seleção.
     */
    protected createDropdown(): void {
        // O dropdown será posicionado no documento principal (não no iframe)
        this.dropdown = document.createElement('div');
        this.dropdown.className = 'ai-sel-dropdown';
        this.dropdown.innerHTML = `
            <button type="button" class="ai-sel-dropdown-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     style="vertical-align: -2px; margin-right: 4px;">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                    <path d="M2 12l10 5 10-5"></path>
                </svg>
                Melhorar com IA
            </button>
        `;
        this.dropdown.style.display = 'none';
        document.body.appendChild(this.dropdown);

        this.dropdown.querySelector('.ai-sel-dropdown-btn')?.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openImprovementModal();
        });
    }

    /**
     * Verifica se há texto selecionado e posiciona o dropdown.
     */
    protected handleSelectionChange(e: MouseEvent): void {
        if (!this.editorDocument) return;

        const selection = this.editorDocument.getSelection();
        if (!selection || selection.isCollapsed || !selection.rangeCount) {
            this.hideDropdown();
            return;
        }

        const selectedText = selection.toString().trim();
        if (selectedText.length < 3) {
            this.hideDropdown();
            return;
        }

        // Capturar o HTML selecionado
        const range = selection.getRangeAt(0);
        const fragment = range.cloneContents();
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(fragment);
        this.selectedHtml = tempDiv.innerHTML;
        this.selectedRange = range.cloneRange();

        // Posicionar o dropdown acima da seleção
        this.positionDropdown(range);
    }

    /**
     * Posiciona o dropdown flutuante acima da seleção no editor.
     */
    protected positionDropdown(range: Range): void {
        if (!this.dropdown) return;

        const iframe = this.getEditorIframe();
        if (!iframe) return;

        const rangeRect = range.getBoundingClientRect();
        const iframeRect = iframe.getBoundingClientRect();

        // Posição relativa ao viewport (considerando iframe offset)
        const top = iframeRect.top + rangeRect.top - 40;
        const left = iframeRect.left + rangeRect.left + (rangeRect.width / 2);

        this.dropdown.style.top = `${Math.max(top, 10)}px`;
        this.dropdown.style.left = `${left}px`;
        this.dropdown.style.transform = 'translateX(-50%)';
        this.dropdown.style.display = 'block';
    }

    protected hideDropdown(): void {
        if (this.dropdown) {
            this.dropdown.style.display = 'none';
        }
    }

    /**
     * Inicializa o modal e seus bindings (lazy init).
     */
    protected initModalIfNeeded(): boolean {
        if (this.initialized) return true;

        try {
            this.modalEl = document.getElementById('ai-selection-improve-modal');
            if (!this.modalEl) return false;

            this.modal = window.$components.firstOnElement(this.modalEl, 'popup');
            if (!this.modal) return false;

            this.selectedTextPreview = this.modalEl.querySelector('#ai-sel-selected-text');
            this.instructionInput = this.modalEl.querySelector('#ai-sel-instruction') as HTMLTextAreaElement;
            this.componentSelect = this.modalEl.querySelector('#ai-sel-component-select') as HTMLSelectElement;
            this.thinkingEl = this.modalEl.querySelector('#ai-sel-thinking');
            this.thinkingText = this.modalEl.querySelector('#ai-sel-thinking-text');
            this.resultSection = this.modalEl.querySelector('#ai-sel-result-section');
            this.resultPreview = this.modalEl.querySelector('#ai-sel-result-preview');
            this.generateBtn = this.modalEl.querySelector('#ai-sel-generate-btn');
            this.applyBtn = this.modalEl.querySelector('#ai-sel-apply-btn');
            this.cancelBtn = this.modalEl.querySelector('#ai-sel-cancel-btn');

            this.setupListeners();
            this.initialized = true;
            return true;
        } catch (e) {
            aiLogger.error('[AiSelectionImprove] Init failed:', e);
            return false;
        }
    }

    protected setupListeners(): void {
        this.generateBtn?.addEventListener('click', () => this.generateImprovement());
        this.applyBtn?.addEventListener('click', () => this.applyImprovement());
        this.cancelBtn?.addEventListener('click', () => this.closeModal());

        // Listeners para botões de ação rápida
        const quickBtns = this.modalEl?.querySelectorAll('.ai-quick-action-btn');
        quickBtns?.forEach(btn => {
            btn.addEventListener('click', () => {
                const prompt = (btn as HTMLElement).dataset.aiQuickPrompt;
                if (prompt && this.instructionInput) {
                    this.instructionInput.value = prompt;
                    this.generateImprovement();
                }
            });
        });

        // Enter no textarea (Ctrl+Enter) gera melhoria
        this.instructionInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                this.generateImprovement();
            }
        });
    }

    /**
     * Abre o modal de melhoria com o texto selecionado.
     */
    protected async openImprovementModal(): Promise<void> {
        this.hideDropdown();

        if (!this.initModalIfNeeded()) {
            window.$events.emit('error', 'Erro ao abrir modal de melhoria.');
            return;
        }

        // Resetar estado
        this.resetModalState();

        // Mostrar preview do texto selecionado
        if (this.selectedTextPreview) {
            this.selectedTextPreview.innerHTML = this.selectedHtml || '<em class="text-muted">Nenhum texto</em>';
        }

        // Carregar componentes se ainda não carregados
        if (this.componentsList.length === 0) {
            await this.loadComponentsList();
        }

        this.modal.show();

        // Focus no campo de instrução
        setTimeout(() => {
            this.instructionInput?.focus();
        }, 200);
    }

    protected resetModalState(): void {
        if (this.instructionInput) this.instructionInput.value = '';
        if (this.thinkingEl) this.thinkingEl.style.display = 'none';
        if (this.resultSection) this.resultSection.style.display = 'none';
        if (this.resultPreview) this.resultPreview.innerHTML = '';
        if (this.generateBtn) this.generateBtn.style.display = '';
        if (this.applyBtn) this.applyBtn.style.display = 'none';
        this.resultHtml = '';
        this.isGenerating = false;
    }

    /**
     * Carrega lista de componentes para o dropdown de referência.
     */
    protected async loadComponentsList(): Promise<void> {
        try {
            const resp = await fetch('/ai/components', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
            });

            if (!resp.ok) return;

            const data = await resp.json();
            if (!data.success || !Array.isArray(data.categories)) return;

            this.componentsList = [];
            for (const cat of data.categories) {
                for (const comp of cat.components) {
                    this.componentsList.push({
                        hash: comp.hash,
                        tag: comp.tag,
                        label: `${cat.label} — ${comp.description}`,
                    });
                }
            }

            // Popular select
            if (this.componentSelect) {
                // Manter a primeira opção
                const firstOption = this.componentSelect.options[0];
                this.componentSelect.innerHTML = '';
                if (firstOption) this.componentSelect.appendChild(firstOption);

                for (const comp of this.componentsList) {
                    const opt = document.createElement('option');
                    opt.value = comp.hash;
                    opt.textContent = comp.label.length > 80
                        ? comp.label.substring(0, 77) + '...'
                        : comp.label;
                    this.componentSelect.appendChild(opt);
                }
            }
        } catch (e) {
            aiLogger.error('[AiSelectionImprove] Failed to load components:', e);
        }
    }


    /**
     * Gera a melhoria via streaming.
     */
    protected async generateImprovement(): Promise<void> {
        if (this.isGenerating) return;

        const instruction = this.instructionInput?.value.trim() || '';
        if (!instruction) {
            window.$events.emit('warning', 'Digite uma instrução para a melhoria.');
            return;
        }

        if (!this.selectedHtml) {
            window.$events.emit('warning', 'Nenhum texto selecionado.');
            return;
        }

        // Cancelar anterior
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        this.isGenerating = true;

        // UI: mostrar loading, exibir o painel de resultado (como container do overlay)
        if (this.resultSection) this.resultSection.style.display = 'flex';
        if (this.thinkingEl) this.thinkingEl.style.display = 'flex';
        if (this.thinkingText) this.thinkingText.textContent = 'Athena está analisando seu texto...';
        
        if (this.generateBtn) this.generateBtn.setAttribute('disabled', 'true');
        if (this.resultPreview) this.resultPreview.innerHTML = '';
        if (this.applyBtn) this.applyBtn.style.display = 'none';

        try {
            const componentHash = this.componentSelect?.value || '';
            const pageId = getPageId();
            
            // Extrair contexto do editor (Draft Context) para ajudar a IA em páginas não salvas
            const editorHtml = this.editorDocument?.body.innerHTML || '';

            const resp = await fetch('/ai/improve-selection', {
                method: 'POST',
                signal: this.abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    selected_html: this.selectedHtml,
                    instruction: instruction,
                    page_id: pageId,
                    component_hash: componentHash || null,
                    editor_context: editorHtml // Enviando o contexto atual do editor
                }),
            });

            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const fullContent = await readSSEStream(resp, {
                onProgress: (data: any) => {
                    if (this.thinkingText) {
                        this.thinkingText.textContent = data.message || 'Processando...';
                    }
                },
                onContentStart: () => {

                    if (this.resultSection) {
                        this.resultSection.style.display = 'flex';
                    }
                },
                onContentChunk: (content: string) => {
                    const trimmed = content.trim();
                    if (trimmed && this.resultPreview) {

                        if (this.thinkingEl && this.thinkingEl.style.display !== 'none') {
                            this.thinkingEl.style.display = 'none';
                        }
                        this.resultPreview.innerHTML = stripChatCodeWrappers(fixCodeBlockEscapes(trimmed));
                    }
                },
            });

            this.resultHtml = stripChatCodeWrappers(fixCodeBlockEscapes(denormalizeHtml(fullContent)));

            if (this.resultHtml && this.hasVisibleContent(this.resultHtml)) {

                if (this.applyBtn) this.applyBtn.style.display = '';
                if (this.generateBtn) this.generateBtn.style.display = 'none';
                window.$events.emit('success', 'Melhoria gerada! Revise e aplique.');
            } else {

                this.resultHtml = '';
                if (this.resultSection) this.resultSection.style.display = 'none';
                if (this.generateBtn) this.generateBtn.style.display = '';
                window.$events.emit('warning', 'A IA não gerou conteúdo. Tente reformular a instrução.');
            }

        } catch (err: any) {
            if (err.name === 'AbortError') return;
            window.$events.emit('error', 'Erro ao gerar melhoria: ' + (err.message || 'Desconhecido'));
        } finally {
            this.isGenerating = false;
            if (this.thinkingEl) this.thinkingEl.style.display = 'none';
            if (this.generateBtn) this.generateBtn.removeAttribute('disabled');
        }
    }

    /**
     * Verifica se uma string HTML contém conteúdo textual visível.
     * Evita aplicar resultados vazios (ex: <p></p>, <br/>, só whitespace).
     */
    protected hasVisibleContent(html: string): boolean {
        if (!html.trim()) return false;
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return (temp.textContent || '').trim().length > 0;
    }

    /**
     * Aplica a melhoria substituindo o conteúdo selecionado no editor.
     * Restaura a seleção e usa editor::insert (TinyMCE API) em vez do
     * deprecated document.execCommand('insertHTML').
     */
    protected applyImprovement(): void {
        if (!this.resultHtml || !this.hasVisibleContent(this.resultHtml)) {
            window.$events.emit('warning', 'Nenhum conteúdo para aplicar. Tente gerar novamente.');
            return;
        }

        // Restaurar seleção no editor para que insertContent saiba onde inserir
        if (this.selectedRange && this.editorDocument) {
            try {
                const selection = this.editorDocument.getSelection();
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(this.selectedRange);
                }
            } catch (e) {
                aiLogger.warn('[AiSelectionImprove] Could not restore selection:', e);
            }
        }

        // Usa editor::insert que chama editor.insertContent() — substitui a seleção atual
        window.$events.emit('editor::insert', { html: this.resultHtml });
        this.closeModal();
        window.$events.emit('success', 'Melhoria aplicada no editor!');
    }

    protected closeModal(): void {
        if (this.modal) {
            this.modal.hide();
        }
        this.hideDropdown();
    }
}
